<template>
	<div v-if="isLoading" class="w-full h-full">
		<div class="flex animate-pulse flex-col items-top h-full space-y-5">
			<div class="w-35 bg-gray-300 h-8 rounded-md" />
			<div class="w-20 bg-gray-300 h-6 rounded-md" />
			<div class="w-full bg-gray-300 h-10 rounded-md" />
			<div class="w-30 bg-gray-300 h-6 rounded-md" />
			<div class="w-full bg-gray-300 h-10 rounded-md" />
		</div>
	</div>
	<div
		v-else
		:class="`relative flex w-full items-start py-6 border-b border-b-gray-lighter`"
	>
		<div
			:class="`${pictureSize} shrink-0 overflow-hidden bg-white mr-4 relative`"
		>
			<NuxtLink
				class="cursor-pointer"
				:to="clickIsValid ? routeToProduct : ''"
				@click="closeModal"
			>
				<NuxtImg
					:src="
						product?.Files?.[0]?.Path
							? product.Files[0].Path + '?format=webp&w=120'
							: defaultImage
					"
					:alt="product.Name"
					:class="`${pictureSize} h-full w-full object-contain object-center`"
					sizes="120px"
					:provider="product?.Files?.[0]?.Path ? undefined : 'storyblok'"
				/>
			</NuxtLink>

			<ProductLabel type="sale" />
			<ProductLabel type="new" />
		</div>
		<div class="flex flex-col gap-2 items-start flex-1">
			<div class="flex flex-col gap-2 w-full">
				<div class="flex justify-between w-full">
					<span class="font-medium text-base">{{ product.Name }}</span>
					<TrashIcon
						class="shrink-0 cursor-pointer text-gray-700 ml-auto h-6 w-6"
						@click="deleteWishlistItem(product.Id)"
					/>
				</div>
				<p v-if="selectedFirstParametric" class="text-sm text-gray-super-dark">
					{{ setName(selectedFirstParametric) }}:
					{{ selectedFirstParametric.Name || selectedFirstParametric.Code }}
				</p>
				<p v-if="selectedSecondParametric" class="text-sm text-gray-super-dark">
					{{ setName(selectedSecondParametric) }}:
					{{ selectedSecondParametric.Name || selectedSecondParametric.Code }}
				</p>
				<PriceGate>
					<Price
						:price="product.PriceIncVat"
						:show-save-text="true"
						:old-price="oldPrice"
					/>
				</PriceGate>
			</div>
			<!-- A bundle is added from its own page: see `isStructureProduct`. -->
			<AddToCart
				v-if="!isStructure && !isMultiPackProduct"
				:wish-product="product"
				:stock="getProductStock(product)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { parametricsSeedToQuery } from "~/utils/parametricsUrl";
import {
	getMultiPackMeta,
	isMultiPackEnabled
} from "~/utils/product/multiPack";
import { isStructureProduct } from "~/utils/product/structureProduct";
import { onMounted, ref, computed } from "vue";
import { TrashIcon } from "@heroicons/vue/24/outline";
import type {
	ParametricListValue,
	ProductItem
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import Price from "./Product/ProductAttributes/Price.vue";
import ProductLabel from "./Product/ProductLabel.vue";
import AddToCart from "./Product/ProductAttributes/AddToCart/AddToCart.vue";

interface Props {
	product: ProductItem;
	pictureSize: string;
	clickIsValid?: boolean;
}

const { product, pictureSize, clickIsValid = true } = defineProps<Props>();

const productStore = useProductStore();

const { getProductStock } = useNorcePwa();
const { oldPrice } = useProductPrices(product);
// const product = ref(props.product);

const defaultImage = computed(() => {
	return productStore.defaultProductImage || "images/product/no_image.jpeg";
});

const localePath = useLocalePath();

const routeToProduct = computed(() => {
	const path = localePath(`/${product?.UniqueName}`);
	if (!product?.ParametricListSeed || !product?.VariantParametricSeed?.length) {
		return path;
	}
	if (allParametricsRef.value.length) {
		const query = parametricsSeedToQuery(
			product.ParametricListSeed,
			allParametricsRef.value
		);
		return Object.keys(query).length ? { path, query } : path;
	}
	return path;
});

const selectedFirstParametric = ref<ParametricListValue>();
const selectedSecondParametric = ref<ParametricListValue>();
const allParametricsRef = ref<ParametricListValue[]>([]);

const { parametricsInformation } = storeToRefs(useProductStore());
const { productTypes } = storeToRefs(useNorceStore());

// `listProductsByIds` carries `Type`, so this check is sync (a listing record
// has none — see `useCatalogAddToCart`).
const { packsEnabled } = usePacksEnabled();

const isStructure = computed(
	() => packsEnabled && isStructureProduct(product, productTypes.value) === true
);

// A multi pack never enters the basket itself; it is picked on its own page.
const multiPackConfig = useMultiPackConfig();
const isMultiPackProduct = computed(
	() =>
		isMultiPackEnabled(multiPackConfig) &&
		!!getMultiPackMeta([product], multiPackConfig, product.PartNo)
);

const { fetchAllParametrics } = useAllParametrics();

onMounted(async () => {
	const allParametrics = await fetchAllParametrics();
	allParametricsRef.value = allParametrics ?? [];

	const parametricsSeed = product?.ParametricListSeed?.split(",");
	if (parametricsSeed && product?.VariantParametricSeed.length) {
		let paramsFromParametricsSeed: number[][] = [];
		parametricsSeed.forEach((paramSeed) => {
			const params = paramSeed.split(":").map(Number);

			paramsFromParametricsSeed.push(params);
		});
		let firstParametric, secondParametric;

		paramsFromParametricsSeed = paramsFromParametricsSeed.filter(
			(paramFromParametricsSeed) => {
				if (paramFromParametricsSeed[0]) {
					return product?.VariantParametricSeed.split(",")
						.map(Number)
						.includes(paramFromParametricsSeed[0]);
				}
			}
		);
		for (const params of paramsFromParametricsSeed) {
			if (params) {
				const selectedParam = allParametrics?.find((param) => {
					return param.ParametricId === +params[0]! && param.Id === +params[1]!;
				});
				if (!secondParametric && firstParametric) {
					secondParametric = selectedParam;
				}
				if (!firstParametric) {
					firstParametric = selectedParam;
				}
			}
		}

		selectedFirstParametric.value = firstParametric;
		selectedSecondParametric.value = secondParametric;
	}
});

const setName = (parametric: ParametricListValue) => {
	return parametricsInformation.value.filter(
		(paramInfo) => paramInfo.Code === parametric?.ParametricCode
	)[0]?.Name;
};

const isLoading = ref(false);

const deleteWishlistItem = async (productId: number) => {
	isLoading.value = true;
	await useWishlistStore().updateWishList(productId);
	isLoading.value = false;
};

const emit = defineEmits(["closeModal"]);

const closeModal = () => {
	return emit("closeModal");
};
</script>
